import type { QueryClient } from '@tanstack/react-query';
import type { AgentList, ManagedAgentRefRequest } from '@/gen-api/models';

type Ref = ManagedAgentRefRequest;
const key = (ref: Ref) => `${ref.ref_type ?? 'tenant'}:${ref.agent_id}`;
export const sameRoster = (a: Ref[], b: Ref[]) =>
  a.length === b.length && a.every((ref, index) => key(ref) === key(b[index]));

/** Rebase only roster changes; do not discard unsaved edits to other fields.
 * Missing catalogue entries are not proof of ineligibility (pagination/RBAC).
 */
export function reconcileRoster(current: Ref[], previous: Ref[], incoming: Ref[], agents: AgentList[]): Ref[] {
  const before = new Set(previous.map(key));
  const after = new Set(incoming.map(key));
  const local = new Set(current.map(key));
  const managerOnly = new Set(
    agents.filter((a) => a.role_classification === 'manager').map((a) => `${a.is_public ? 'public' : 'tenant'}:${a.id}`)
  );
  const rebased = sameRoster(current, previous)
    ? incoming
    : [
        ...current.filter((ref) => !before.has(key(ref)) || after.has(key(ref))),
        ...incoming.filter((ref) => !before.has(key(ref)) && !local.has(key(ref))),
      ];
  return rebased.filter((ref) => !managerOnly.has(key(ref)));
}

/** Generated keys are workspace-unqualified; workspace switching reloads the
 * app. Never refetch an old mutation's queries with a new workspace's header.
 * Match endpoint families, not a string-prefix queryKey (which is exact).
 */
export async function refreshRoleAffectedQueries(
  client: QueryClient,
  apiBase: string | undefined,
  workspaceId: string | undefined,
  activeWorkspaceId: string | undefined
) {
  if (!workspaceId || workspaceId !== activeWorkspaceId) return;
  const prefix = `${apiBase}/api/v1/`;
  await client.invalidateQueries({
    predicate: ({ queryKey }) => {
      const url = queryKey[0];
      if (typeof url !== 'string' || !url.startsWith(prefix)) return false;
      const path = url.slice(prefix.length);
      return (
        /^(tenant|public)\/(?:[^/]+\/(?:versions\/)?)*$/.test(path) || path === 'hybrid/' || path === 'team-graph/'
      );
    },
    // Active editors refresh now; cached inactive editors refetch on mount.
    refetchType: 'active',
  });
}
